import { useMemo } from "react";
import { makeUrl, routes } from "../../lib/urls";
import { DonutChart } from "../DonutChart";
import type { StatusSegment } from "../StatusBreakdownChart";
import type { AiProviderAllocationRow } from "./aiProviderMonitoring";
import { formatEstimatedCostUsd } from "./aiUsage";

const AVAILABLE_KEY = "__available__";

type Props = {
  rows: readonly AiProviderAllocationRow[];
  colorMap: ReadonlyMap<string, string>;
  trackColorMap: ReadonlyMap<string, string>;
};

export function AiProviderSpendDonut({ rows, colorMap, trackColorMap }: Props) {
  const sliceRows = useMemo(
    () => rows.filter((row) => row.kind !== "total"),
    [rows]
  );
  const spaceIds = useMemo(
    () =>
      new Set(rows.filter((row) => row.kind === "space").map((row) => row.id)),
    [rows]
  );
  const totalSpend = useMemo(
    () => sliceRows.reduce((sum, row) => sum + (row.spendUsd ?? 0), 0),
    [sliceRows]
  );
  const totalCapacity = useMemo(
    () =>
      sliceRows.reduce(
        (sum, row) => sum + (row.limitAmount ?? row.spendUsd ?? 0),
        0
      ),
    [sliceRows]
  );
  const providerCap =
    rows.find((row) => row.kind === "total")?.limitAmount ?? null;
  // Size the ring to the provider cap so spend fills it only as the cap is approached.
  const donutTotal = Math.max(providerCap ?? 0, totalCapacity);
  const segments = useMemo<StatusSegment[]>(() => {
    const base: StatusSegment[] = sliceRows.map((row) => ({
      key: row.id,
      label: row.label,
      count: row.spendUsd ?? 0,
      capacity: row.limitAmount ?? row.spendUsd ?? 0,
      color:
        row.kind === "personal"
          ? "var(--gray-600)"
          : row.kind === "unattributed"
            ? "var(--gray-400)"
            : (colorMap.get(row.id) ?? "var(--alpha8)"),
      trackColor: trackColorMap.get(row.id) ?? "var(--alpha4)",
      caption:
        row.limitAmount != null
          ? `of ${formatEstimatedCostUsd(row.limitAmount)}`
          : "No limit",
    }));
    const remaining = donutTotal - totalCapacity;
    if (remaining > 0) {
      // Leftover cap not assigned to any space, shown as a hoverable slice; count === capacity keeps it solid.
      base.push({
        key: AVAILABLE_KEY,
        label: "Available",
        count: remaining,
        capacity: remaining,
        color: "var(--alpha4)",
        caption: `of ${formatEstimatedCostUsd(providerCap!)}`,
        hideFromLegend: true,
      });
    }
    return base;
  }, [
    sliceRows,
    colorMap,
    trackColorMap,
    donutTotal,
    totalCapacity,
    providerCap,
  ]);

  return (
    <DonutChart
      segments={segments}
      total={totalSpend}
      capacityTotal={donutTotal}
      formatValue={formatEstimatedCostUsd}
      centerLabel="All spend"
      centerCaption={
        providerCap != null
          ? `of ${formatEstimatedCostUsd(providerCap)}`
          : "spent"
      }
      strokeWidth={8}
      scrollableLegend
      placeholder={rows.length === 0}
      segmentHref={(s) =>
        spaceIds.has(s.key)
          ? makeUrl(routes.space, { spaceId: s.key })
          : undefined
      }
    />
  );
}
